import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { TranslateService } from '@root/shared/service/translate.service';

interface IHttpResponse {
  error_code: number;
  error_msg: string;
  data: any;
}

interface ISetAutoAcceptReservation {
  status: number;
}

interface IGetThirdpartyList {
  page: number;
  page_size: number;
  username?: string;
  thirdparty_type?: number;
  format?: string;
}

interface IGetThirdpartyHouseList {
  page: number;
  page_size: number;
  thirdparty_type: number;
  house_title?: string;
  thirdparty_account_id?: number;
}

interface IUnbindRelation {
  house_id: number;
  house_price_id: number;
}

interface IUnbindHouseTypeRelation {
  house_type_id: number;
  house_type_price_id: number;
}

interface IReadThirdpartyHouse {
  house_id: number;
}

interface IReadThirdpartyHouseType {
  house_type_id: number;
}

interface ISyncerHouse {
  thirdparty_type?: number;
  thirdparty_account_id?: number;
}

const HAD_THIRDPARTY_API = '/api/thirdparty/had_thirdparty';
const SYNCER_HOUSE_API = '/api/thirdparty/syncer_house';
const AUTO_ACCEPT_RESERVATION_DETAIL_API = '/api/auto_accept_reservation/detail';
const SET_AUTO_ACCEPT_RESERVATION_API = '/api/auto_accept_reservation/set';
const GET_THIRDPARTY_LIST_API = '/api/thirdparty/list';
const GET_THIRDPARTY_HOUSE_LIST_API = '/api/thirdparty/thirdparty_house_list';
const UNBIND_RELATION_API = '/api/house_relation/unbind_relation';
const UNBIND_HOUSE_TYPE_RELATION_API = '/api/house_type/unbind_relation';
const READ_THIRDPARTY_HOUSE_API = '/api/house/read_thirdparty_house';
const REFRESH_THIRDPARTY_HOUSE_TYPE = '/api/house_type/read_thirdparty_house';
const REFRESH_THIRDPARTY_HOUSE_OF_CHANNEL = '/api/thirdparty/read_unbind_thirdparty_house';

@Injectable()
export class ChannelHouseService {
  constructor(private http: HttpClient, private modal: NzModalService, private t: TranslateService) {}

  // 获得已有的渠道
  getCurrentAccountBindedThirdparties() {
    return this.http.get(HAD_THIRDPARTY_API) as Observable<IHttpResponse>;
  }

  /** 获取渠道账号列表 */
  getThirdpartyList(params: IGetThirdpartyList) {
    const query = new HttpParams()
      .append('page', `${params.page}`)
      .append('page_size', `${params.page_size}`)
      .append('username', `${params.username}`)
      .append('thirdparty_type', `${params.thirdparty_type}`)
      .append('format', `${params.format}`);
    return this.http.get(GET_THIRDPARTY_LIST_API, {
      params: query,
    }) as Observable<IHttpResponse>;
  }

  // 账号同步全部房源
  syncerAllHouse(params: ISyncerHouse) {
    return this.http.post(SYNCER_HOUSE_API, params) as Observable<IHttpResponse>;
  }

  // 自动接单详情
  getAutoAcceptReservationDetail() {
    return this.http.get(AUTO_ACCEPT_RESERVATION_DETAIL_API) as Observable<IHttpResponse>;
  }

  // 自动接单设置
  setAutoAcceptReservation(params: ISetAutoAcceptReservation) {
    return this.http.post(SET_AUTO_ACCEPT_RESERVATION_API, params) as Observable<IHttpResponse>;
  }

  // 渠道房源列表
  getThirdpartyHouseList(params: IGetThirdpartyHouseList) {
    const query = new HttpParams()
      .append('page', `${params.page}`)
      .append('page_size', `${params.page_size}`)
      .append('thirdparty_type', `${params.thirdparty_type}`)
      .append('house_title', `${params.house_title}`)
      .append('thirdparty_account_id', `${params.thirdparty_account_id}`);
    return this.http.get(GET_THIRDPARTY_HOUSE_LIST_API, { params: query }) as Observable<IHttpResponse>;
  }

  // 解除房源关联
  unbindRelation(params: IUnbindRelation) {
    return this.http.post(UNBIND_RELATION_API, params) as Observable<IHttpResponse>;
  }

  // 解除房型关联
  unbindHouseTypeRelation(params: IUnbindHouseTypeRelation) {
    return this.http.post(UNBIND_HOUSE_TYPE_RELATION_API, params) as Observable<IHttpResponse>;
  }

  // 刷新房间
  readThirdpartyHouse(params: IReadThirdpartyHouse) {
    return this.http.post(READ_THIRDPARTY_HOUSE_API, params) as Observable<IHttpResponse>;
  }

  /** 刷新房型 */
  requestRefreshThirdpartyHouseType(params: IReadThirdpartyHouseType) {
    return this.http.post(REFRESH_THIRDPARTY_HOUSE_TYPE, params) as Observable<IHttpResponse>;
  }

  /** 刷新渠道下所有的房源信息 */
  requestRefreshThirdpartyHouseOfChannel(params) {
    return this.http.post(REFRESH_THIRDPARTY_HOUSE_OF_CHANNEL, params) as Observable<IHttpResponse>;
  }

  displaySyncListingWarning(): Observable<boolean> {
    return new Observable((observer) => {
      const modal = this.modal.warning({
        nzTitle: this.t.t('channel-start_link_waring_title'),
        nzContent: this.t.t('channel-start_link_waring_content'),
        nzWidth: 545,
        nzOkText: this.t.t('calendar-filter_html_sync_now'),
      });

      modal.afterClose.subscribe(() => {
        observer.next(true);
        observer.complete();
      });
    });
  }
}
